import React, { useMemo, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useRunData, useRunPicker, useTrends } from "./useRunData";
import { useChartTheme, rateChartOptions, SERIES_COLORS } from "./chartTheme";
import { pct, runLabel } from "./format";
import { PendingBox, PendingRow, Spinner } from "./Pending";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TOP_N = 5;

export default function TabCompetitors() {
  const { report, reportId } = useOutletContext();
  const [runNumber, setRunNumber, runs] = useRunPicker(report);
  const { data, pending, error } = useRunData(reportId, runNumber, { includeAggregationsOnly: true });
  const { runs: trend, pending: trendPending } = useTrends(reportId, report.updatedAt);
  const chartRef = useRef(null);
  const theme = useChartTheme(chartRef);

  const agg = data?.aggregations || {};
  const competitors = agg.allCompetitors || [];
  const opps = agg.topicOpportunities || [];
  const summary = runs.find((r) => r.runNumber === runNumber) || {};
  const total = summary.totalItems || 0;
  const share = (c) => (c.rate != null ? pct(c.rate) : total ? pct((c.count || 0) / total) : "—");

  // Share of voice over time: the client plus the top competitors of the latest run.
  const chart = useMemo(() => {
    if (trend.length < 2) return null;
    const latest = trend[trend.length - 1];
    const top = (latest.allCompetitors || []).slice()
      .sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, TOP_N).map((c) => c.name);
    const rateFor = (run, name) => {
      const c = (run.allCompetitors || []).find((x) => x.name === name);
      if (!c) return 0;
      if (c.rate != null) return c.rate;
      return run.totalItems ? (c.count || 0) / run.totalItems : 0;
    };
    return {
      labels: trend.map(runLabel),
      datasets: [
        { label: `${report.clientName} (client)`, borderWidth: 3, tension: 0.2,
          data: trend.map((r) => Math.round((r.clientMentionRate || 0) * 100)),
          borderColor: SERIES_COLORS[0], backgroundColor: SERIES_COLORS[0] },
        ...top.map((name, i) => ({
          label: name, tension: 0.2, borderDash: [4, 3],
          data: trend.map((r) => Math.round(rateFor(r, name) * 100)),
          borderColor: SERIES_COLORS[(i + 1) % SERIES_COLORS.length],
          backgroundColor: SERIES_COLORS[(i + 1) % SERIES_COLORS.length],
        })),
      ],
    };
  }, [trend, report.clientName]);
  const chartOptions = useMemo(() => rateChartOptions(theme), [theme]);

  return (
    <div>
      <div className="run-picker">
        <label>Run:
          <select value={runNumber || ""} onChange={(e) => setRunNumber(Number(e.target.value))}>
            {runs.slice().reverse().map((r) => (
              <option key={r.runNumber} value={r.runNumber}>#{r.runNumber}</option>
            ))}
          </select>
        </label>
      </div>

      {(chart || (trendPending && runs.length >= 2)) && (
        <section className="overview-chart" ref={chartRef} style={{ marginBottom: 24 }}>
          <h3>Share of voice over time</h3>
          <div className="overview-chart-wrap">
            {chart ? <Line data={chart} options={chartOptions} /> : <PendingBox height={260} />}
          </div>
        </section>
      )}

      {error && <div className="error-message">{error}</div>}

      <h3>Client vs competitors</h3>
      <table className="runs-table">
        <thead><tr><th>Name</th><th>Mentions</th><th>Share of responses</th></tr></thead>
        <tbody>
          <tr>
            <td><strong>{report.clientName} (client)</strong></td>
            <td>{summary.clientMentionedCount || 0}</td>
            <td>{total ? pct((summary.clientMentionedCount || 0) / total) : "—"}</td>
          </tr>
          {pending && <PendingRow cols={3} />}
          {!pending && competitors.map((c) => (
            <tr key={c.name}><td>{c.name}</td><td>{c.count}</td><td>{share(c)}</td></tr>
          ))}
          {!pending && competitors.length === 0 && <tr><td colSpan="3">No competitors detected.</td></tr>}
        </tbody>
      </table>

      <h3 style={{ marginTop: 24 }}>Topic opportunities</h3>
      {pending ? <Spinner size={20} /> : (
        <ul>
          {opps.map((o) => (
            <li key={o.keyword || o.prompt}>
              <strong>{o.keyword || o.prompt}</strong> — {o.competitors.join(", ")} ({o.totalResponses} responses, 0 client mentions)
            </li>
          ))}
          {opps.length === 0 && <li>No gaps identified.</li>}
        </ul>
      )}
    </div>
  );
}
